import React from 'react'
import PropTypes from 'prop-types'

import { Form, FormControl, Glyphicon, Alert } from 'react-bootstrap'
import InputField from './../../../FormInputComponents/InputField'

import CategorySuggestion from './../../../Suggestion/CategorySuggestion'
import MerchantSuggestion from './../../../Suggestion/MerchantSuggestion'

import useFormMerchantCategoryRegexFilds from './useFormMerchantCategoryRegexFilds'
import useModalUISettings from './../../Common/useModalUISettings'
import useFormButtons from './../../Common/useFormButtons'
import useMode from './../../Common/useMode'

import NoteFields from './../../Common/NoteFields'
import CommonRuleModalButtons from './../../Common/CommonRuleModalButtons'

export default function MerchantCategoryRegexRuleModal({ value, isRequest, onOkHandler, onRejectHandler, isReadOnly }) {
  const {
    id,
    merchantId,
    merchantName,
    categoryId,
    categoryFullPath,
    includeParam,
    excludeParam,
    ruleType,
    isExcludeRule,
    request_notes,
    review_notes
  } = value

  const mode = useMode(isRequest, isReadOnly, id)

  const initValues = {
    id: id ?? null,
    merchantId: merchantId ?? null,
    merchantName: merchantName ?? null,
    categoryId: categoryId ?? null,
    categoryFullPath: categoryFullPath ?? null,

    includeParam: includeParam ?? null,
    excludeParam: excludeParam ?? null,
    ruleType: ruleType ?? 'DESCRIPTION',
    isExcludeRule: isExcludeRule ?? false,

    request_notes: request_notes ?? null,
    review_notes: review_notes ?? null
  }

  const {
    values,
    isFormChanged,
    handleFieldChange,
    handleMerchantChange,
    handleCategoryChange
  } = useFormMerchantCategoryRegexFilds(initValues, mode)

  const isAllFieldsFilled = values.merchantId && values.categoryId && values.includeParam && values.ruleType
  const fieldsNames = 'Merchant, Category and other parameters'
  const isRuleActiveFunc = rule => rule.isActive && rule.categoryIsActive && rule.merchantIsActive
  const { ruleInfoMessage, ruleInfoStyle, okButtonName, isOkButtonDisabled, isFieldsDisabled } = useModalUISettings({
    prevRuleId: values.id,
    isAllFieldsFilled,
    isFormChanged,
    fieldsNames,
    isRuleActiveFunc,
    mode
  })

  const sentFieldsForRules = ['merchantId', 'categoryId', 'includeParam', 'excludeParam', 'ruleType', 'isExcludeRule']
  const sentFieldsForRequests = ['merchantName', 'categoryFullPath', 'request_notes', 'review_notes']
  const { handleOkButton, handleRejectButton, handleCancelButton } = useFormButtons({
    onOkHandler,
    onRejectHandler,
    values,
    sentFieldsForRules,
    sentFieldsForRequests,
    isRequest
  })

  return (
    <Form className="text-left" horizontal>
      <InputField validationState={values.merchantId === undefined ? 'error' : null} required label={'Merchant'}>
        <MerchantSuggestion
          selectedId={values.merchantId}
          selectedLabel={values.merchantName}
          changeCallback={handleMerchantChange}
          readOnly={mode.isRequestApprove || mode.isRequestView}
        />
      </InputField>
      <InputField validationState={values.categoryId === undefined ? 'error' : null} required label={'Category'}>
        <CategorySuggestion
          selectedId={values.categoryId}
          selectedLabel={values.categoryFullPath}
          changeCallback={handleCategoryChange}
          readOnly={mode.isRequestApprove || mode.isRequestView}
        />
      </InputField>

      <InputField
        id="includeParam"
        label="Include param"
        type="input"
        value={values.includeParam || ''}
        onChange={handleFieldChange}
        validationState={values.includeParam === '' ? 'error' : null}
        disabled={mode.isRequestApprove || mode.isRequestView}
        required
      />
      <InputField
        id="excludeParam"
        label="Exclude param"
        type="input"
        value={values.excludeParam || ''}
        onChange={handleFieldChange}
        disabled={mode.isRequestApprove || mode.isRequestView || isFieldsDisabled}
      />
      <InputField label="Rule Type" required>
        <FormControl
          id="ruleType"
          componentClass="select"
          placeholder="select"
          defaultValue={values.ruleType}
          onChange={handleFieldChange}
          disabled={mode.isRequestApprove || mode.isRequestView}
        >
          <option value="DESCRIPTION">DESCRIPTION</option>
          <option value="PARSED_CATEGORY">PARSED_CATEGORY</option>
        </FormControl>
      </InputField>

      <InputField
        id="isExcludeRule"
        label="Is Exclude Rule"
        type="CheckBox"
        checked={values.isExcludeRule || false}
        onChange={handleFieldChange}
        disabled={mode.isRequestApprove || mode.isRequestView || isFieldsDisabled}
      />

      {!mode.isRequestView && (
        <Alert bsStyle={ruleInfoStyle}>
          <Glyphicon className="padding-rigt-standard" glyph="info-sign" />
          {ruleInfoMessage}
        </Alert>
      )}

      <NoteFields
        mode={mode}
        request_notes={values.request_notes}
        review_notes={values.review_notes}
        handleFieldChange={handleFieldChange}
      />

      <CommonRuleModalButtons
        handleOkButton={handleOkButton}
        handleRejectButton={handleRejectButton}
        handleCancelButton={handleCancelButton}
        mode={mode}
        okButtonName={okButtonName}
        isOkButtonDisabled={isOkButtonDisabled}
      />
    </Form>
  )
}

MerchantCategoryRegexRuleModal.defaultProps = {
  value: {
    id: null,
    merchantId: undefined,
    merchantName: null,
    categoryId: undefined,
    categoryFullPath: null,

    includeParam: null,
    excludeParam: null,
    ruleType: null,
    isExcludeRule: null,

    request_notes: null,
    review_notes: null
  },
  isRequest: false,
  isReadOnly: false
}

MerchantCategoryRegexRuleModal.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.number,
    merchantId: PropTypes.number,
    merchantName: PropTypes.string,
    categoryId: PropTypes.number,
    categoryFullPath: PropTypes.string,

    includeParam: PropTypes.string,
    excludeParam: PropTypes.string,
    ruleType: PropTypes.string,
    isExcludeRule: PropTypes.bool,

    request_notes: PropTypes.string,
    review_notes: PropTypes.string
  }),

  onOkHandler: PropTypes.func.isRequired,
  onRejectHandler: PropTypes.func,

  isRequest: PropTypes.bool,
  isReadOnly: PropTypes.bool
}
